/**
 * Section Layout Component
 * 
 * This component provides a consistent grid layout for individual sections of the page.
 * It uses Material UI's Grid system to create responsive section layouts.
 */

import React from 'react';
import { Grid, Box, Typography, Divider } from '@mui/material';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import { fadeIn, slideInFromBottom } from '../../utils/animations';

// Styled section container with animation
const StyledSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(6),
  animation: `${fadeIn} 0.8s ease-out`,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '40px',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '2px',
  }
}));

// Styled section title with animation
const SectionTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  marginBottom: theme.spacing(3),
  position: 'relative',
  animation: `${slideInFromBottom} 0.6s ease-out`,
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -8,
    left: theme.spacing(2),
    width: '60px',
    height: '3px',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '2px',
  }
}));

// Styled divider with custom appearance
const StyledDivider = styled(Divider)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  backgroundColor: 'rgba(184, 187, 38, 0.15)',
  height: '1px',
}));

/**
 * SectionLayout component that provides a consistent grid structure for page sections
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Section title
 * @param {string} props.id - Section ID for navigation
 * @param {React.ReactNode} props.children - Section content
 * @param {boolean} props.divider - Whether to show a divider below the title
 * @param {Object} props.spacing - Grid spacing configuration
 * @returns {React.ReactElement} Rendered component
 */
const SectionLayout = ({ 
  title, 
  id, 
  children, 
  divider = true, 
  spacing = { xs: 2, md: 3 } 
}) => {
  return (
    <StyledSection id={id} component="section">
      {title && (
        <>
          <SectionTitle variant="h4" component="h2">
            {title}
          </SectionTitle>
          {divider && <StyledDivider />}
        </>
      )}
      
      <Grid container spacing={spacing}>
        {children}
      </Grid>
    </StyledSection>
  );
};

SectionLayout.propTypes = {
  title: PropTypes.string,
  id: PropTypes.string,
  children: PropTypes.node.isRequired,
  divider: PropTypes.bool,
  spacing: PropTypes.object
};

export default SectionLayout;
